import { NavLink, useLocation } from "react-router-dom";
import { useMemo } from "react";
import { Plus } from "lucide-react";
import { cn } from "@/lib/utils";
import { flattenLeaves, innovationNavTree } from "./nav-config";

/** 顶部一级 Tab / 次级筛选 — 结构与知识库 `knowledge/page.tsx` 一致 */

function topicLeaves() {
  return flattenLeaves(innovationNavTree).filter((l) => l.path.startsWith("/innovation/topic/"));
}

function suggestionLeaves() {
  return flattenLeaves(innovationNavTree).filter((l) => l.path.startsWith("/innovation/suggestions/"));
}

function reviewSegments(kind: string) {
  return flattenLeaves(innovationNavTree).filter((l) => l.path.startsWith(`/innovation/review/${kind}/`));
}

const reviewKinds = [
  { label: "项目评审", kind: "project", entry: "/innovation/review/project/key-info" },
  { label: "专利评审", kind: "patent", entry: "/innovation/review/patent/key-info" },
  { label: "论文评审", kind: "paper", entry: "/innovation/review/paper/key-info" },
];

/** 知识库「全部 / 我创建的」同款 pill */
function filterPillCls(active: boolean) {
  return cn(
    "rounded-full border px-3.5 py-2 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
    active
      ? "border-primary bg-primary text-primary-foreground shadow-sm"
      : "border-border bg-background text-muted-foreground hover:bg-accent hover:text-accent-foreground",
  );
}

/** 知识库详情页内容筛选同款（略小字号时可沿用同一套语义） */
function filterChipCls(active: boolean) {
  return cn(
    "rounded-full border px-3 py-1.5 text-xs font-medium transition-colors md:px-3.5 md:py-2 md:text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
    active
      ? "border-primary bg-primary text-primary-foreground shadow-sm"
      : "border-border bg-background text-muted-foreground hover:bg-accent hover:text-accent-foreground",
  );
}

const mainTabCls = (active: boolean) =>
  cn(
    "text-[18px] transition-colors",
    active ? "font-semibold text-foreground" : "font-normal text-muted-foreground hover:text-foreground",
  );

export default function InnovationTopNav() {
  const { pathname } = useLocation();

  const section = useMemo(() => {
    if (pathname === "/innovation" || pathname === "/innovation/") return "overview";
    if (pathname.startsWith("/innovation/topic")) return "topic";
    if (pathname.startsWith("/innovation/review")) return "review";
    if (pathname.startsWith("/innovation/suggestions")) return "suggestions";
    return "overview";
  }, [pathname]);

  const reviewKind = pathname.match(/\/innovation\/review\/(project|patent|paper)\//)?.[1] ?? null;

  const showSecondary = section === "topic" || section === "review" || section === "suggestions";

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4 rounded-3xl border border-white/60 bg-white py-3 backdrop-blur">
        <div className="flex items-center gap-8 px-2">
          <NavLink to="/innovation" end className={({ isActive }) => mainTabCls(isActive)}>
            概览
          </NavLink>
          <NavLink to="/innovation/records" className={({ isActive }) => mainTabCls(isActive)}>
            评价记录
          </NavLink>
        </div>
        <NavLink
          to="/innovation/create"
          className="inline-flex h-10 shrink-0 items-center gap-2 rounded-lg bg-primary px-4 text-sm font-medium text-primary-foreground shadow-sm transition-colors hover:opacity-90"
        >
          <Plus className="h-4 w-4" />
          新建
        </NavLink>
      </div>

      {showSecondary && (
        <div className="rounded-2xl border border-border bg-card p-4 shadow-sm">
          {section === "topic" && (
            <div className="flex flex-wrap gap-2">
              {topicLeaves().map((t) => (
                <NavLink key={t.path} to={t.path} className={({ isActive }) => filterPillCls(isActive)}>
                  {t.label}
                </NavLink>
              ))}
            </div>
          )}

          {section === "review" && (
            <div className="space-y-3">
              <div className="flex flex-wrap gap-2">
                {reviewKinds.map((rk) => (
                  <NavLink
                    key={rk.kind}
                    to={rk.entry}
                    className={() => filterPillCls(Boolean(pathname.includes(`/review/${rk.kind}/`)))}
                  >
                    {rk.label}
                  </NavLink>
                ))}
              </div>
              {reviewKind && (
                <div className="flex flex-wrap gap-2 overflow-x-auto pb-1 md:flex-wrap">
                  {reviewSegments(reviewKind).map((s) => (
                    <NavLink key={s.path} to={s.path} className={({ isActive }) => filterChipCls(isActive)}>
                      {s.label}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          )}

          {section === "suggestions" && (
            <div className="flex flex-wrap gap-2">
              {suggestionLeaves().map((s) => (
                <NavLink key={s.path} to={s.path} className={({ isActive }) => filterPillCls(isActive)}>
                  {s.label}
                </NavLink>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
